//@ts-check
import React from 'react';
import { useCheckIfUserIsInUS, useCountryCode } from '../../../_shared/react-hooks';
import { SHIPPING_MORE_INFO_LINK } from '../../../../utils/shipping';
import { CurrencyState } from 'src/scripts/utils/currency';
import { observer } from 'mobx-react-lite';
import { downcaseObjectKeys } from '../../../../utils/helpers';
import { getIntlMinimumShippingThreshold } from 'src/scripts/utils/location';
import Reshop from '../../../_shared/Reshop';
import { defaultReshopPdpText } from 'src/scripts/utils/languages';
import { sddState } from '../Sdd/sddState';
import { SddMessaging } from '../Sdd/SddMessaging';
import './style.scss';

const isReshopEnabled = ({ isDomestic }: { isDomestic: boolean }) => {
  const { enabled, enableCartPDPMessaging } = window.getSiteSettings().reshop;
  return isDomestic && enabled && enableCartPDPMessaging;
};

let ReturnsSuggestion = ({ isFinalSale, isExchangeOnly }: { isFinalSale: boolean; isExchangeOnly: boolean }) => {
  const isDomestic = useCheckIfUserIsInUS();
  const currentCountryCode = useCountryCode();
  const intlMinimumShippingThreshold = getIntlMinimumShippingThreshold(CurrencyState.currencyCode);

  const { returnsMessaging: unsanitizedReturnMessaging, customReturnsMessagingEnabled } = window.getSiteSettings();
  const returnMessaging = downcaseObjectKeys(unsanitizedReturnMessaging) as ReturnsMessagingJSON;

  const reshopEnabled = isReshopEnabled({ isDomestic });
  const getReturnCopy = () => {
    if (intlMinimumShippingThreshold && customReturnsMessagingEnabled) {
      // reshopPdp should be configured in shop.metafields.returns.messaging
      // reshopEnabled - only for Domestic users
      const CountrySpecificReturnMessaging = !reshopEnabled
        ? returnMessaging[currentCountryCode.toLowerCase()]?.pdp || returnMessaging.default.pdp
        : returnMessaging['us']?.reshopPdp || defaultReshopPdpText;
      return (
        <ReturnsCopy
          text={CountrySpecificReturnMessaging.replace('[$X]', intlMinimumShippingThreshold)}
          shouldShowReadMore={false}
          isDomestic={isDomestic}
        />
      );
    } else if (!isDomestic && intlMinimumShippingThreshold) {
      // Old implementation for INTL if Custom return messaging is disabled
      return (
        <ReturnsCopy
          text={`Free Shipping Over ${intlMinimumShippingThreshold} and Easy Returns.`}
          shouldShowReadMore={false}
          isDomestic={isDomestic}
        />
      );
    } else {
      // Old implementation for US if Custom return messaging is disabled
      return (
        <ReturnsCopy
          text={reshopEnabled ? defaultReshopPdpText : 'Free Shipping & Free Returns'}
          shouldShowReadMore={false}
          isDomestic={isDomestic}
        />
      );
    }
  };

  const returnsCopy = getReturnCopy();
  const returnsMessage = isFinalSale ? 'final sale' : isExchangeOnly ? 'exchange only' : '';
  const shouldDisplaySddMessaging = isDomestic && sddState.enabled;

  return returnsMessage ? (
    <div className="icons-box__item">
      <div className="returns-suggestion-container">
        <p className="pdp_feature_box_body_copy">
          This item is {returnsMessage}.{' '}
          <a href="/pages/returns-exchanges" target="_blank" rel="noopener">
            <u>More info</u>
          </a>
        </p>
        {shouldDisplaySddMessaging && <SddMessaging />}
      </div>
    </div>
  ) : (
    <div className="icons-box__item">
      <div className="returns-suggestion-container">
        <div className="returns-suggestion-content">
          <p className="pdp_feature_box_body_copy">{returnsCopy}</p>
        </div>
        {shouldDisplaySddMessaging && <SddMessaging />}
      </div>
    </div>
  );
};

ReturnsSuggestion = observer(ReturnsSuggestion);
export { ReturnsSuggestion };

const ReturnsCopy = ({
  text,
  shouldShowReadMore = true,
  isDomestic,
}: {
  text: string;
  shouldShowReadMore?: boolean;
  isDomestic?: boolean;
}) => {
  const reshopEnabled = isReshopEnabled({ isDomestic: !!isDomestic });
  const textCopy = (
    <>
      {text} {reshopEnabled ? <Reshop location="pdp" /> : null}
    </>
  );
  return (
    <span className="returns-copy">
      <span className="alo-truck-icon"></span>
      <span>
        {textCopy}
        {shouldShowReadMore && (
          <a className="moreInfo" href={SHIPPING_MORE_INFO_LINK}>
            <u>Read more</u>
          </a>
        )}
      </span>
    </span>
  );
};
